<!--
This is a wrapper for LineChart and BarChart to provide commonly used
features such as a header, description, a NumeraiLoader, cumulative sum, etc.
A NumeraiLoader cannot be provided by LineChart because
it directly extends the Line class from vue-chartjs and there
are conflicts with the canvas element.
-->
<template>
  <div class="numerai-paper pa-4">
    <div class="d-flex justify-space-between">
      <div>
        <h2 v-if="header" class="numerai-section-heading">
          {{ enableCumsum && isCumulativeDisplay ? 'Cumulative' : '' }}
          {{ header }}
        </h2>
        <div v-if="description" class="description">
          {{ description }}
        </div>
      </div>
      <div>
        <slot name="header-right"></slot>
        <v-menu
          v-if="enableSelector"
          open-on-hover
          left
          bottom
          offset-y
          :dark="isDarkMode"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              text
              v-bind="attrs"
              class="numerai-button button-subtle button-control mr-3"
              v-on="on"
            >
              {{ selectedValue }}
              <v-icon class="select-chevron">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list :dark="isDarkMode">
            <v-list-item-group v-model="selectorIndex">
              <v-list-item
                v-for="(tab, index) in selectorItems"
                :key="index"
                :disabled="index === selectorIndex"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ tab }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
        <v-menu
          v-if="enableCumsum || $slots['menu-items']"
          left
          bottom
          offset-y
          :dark="isDarkMode"
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon
                :color="
                  isDarkMode ? 'var(--color-secondary)' : 'var(--color-dark)'
                "
              >
                mdi-settings
              </v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item>
                <v-list-item-action>
                  <v-switch v-model="useBarChart" color="green" dense />
                </v-list-item-action>
                <v-list-item-title>Bar Chart</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="enableCumsum">
                <v-list-item-action>
                  <v-switch v-model="isCumulativeDisplay" color="green" dense />
                </v-list-item-action>
                <v-list-item-title>Cumulative</v-list-item-title>
              </v-list-item>

              <slot name="menu-items"></slot>
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>
    <div v-if="loading">
      <NumeraiLoader />
    </div>
    <LineChart
      v-else-if="!useBarChart"
      v-bind="modifiedChartOptions"
      :is-dark-mode="isDarkMode"
    />
    <BarChart v-else v-bind="modifiedChartOptions" :is-dark-mode="isDarkMode" />
    <v-slider
      v-if="!loading && enableZoom && zoomMax > 0"
      v-model="zoomMin"
      :min="0"
      :max="zoomMax"
      range
      class="align-center"
      :color="isDarkMode ? 'var(--color-secondary)' : 'var(--color-dark)'"
    >
      <template v-slot:prepend
        ><p
          v-if="zoomMin == 0"
          class="mb-0 mr-2"
          :style="{
            color: isDarkMode ? 'var(--color-secondary)' : 'var(--color-dark)',
          }"
        >
          Zoom
        </p>
        <button
          v-else
          class="numerai-button pa-1"
          style="min-height: 0px"
          @click="zoomMin = 0"
        >
          Reset
        </button>
      </template>
    </v-slider>
    <slot name="footer"></slot>
  </div>
</template>

<script>
import BarChart from '@/components/charts/BarChart'
import LineChart from '@/components/charts/LineChart'
import NumeraiLoader from '@/components/shared/NumeraiLoader'
import { mapGetters } from 'vuex'
import ls from 'local-storage'

export default {
  components: {
    BarChart,
    LineChart,
    NumeraiLoader,
  },
  props: {
    header: { type: String, default: null },
    description: { type: String, default: null },
    loading: { type: Boolean, default: false },
    enableCumsum: { type: Boolean, default: false },
    enableZoom: { type: Boolean, default: false },
    enableSelector: { type: Boolean, default: false },
    chartOptions: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isCumulativeDisplay: false,
      zoomMin: 0,
      selectorIndex: 0,
      useBarChart: false,
    }
  },
  computed: {
    ...mapGetters(['isDarkMode']),
    modifiedChartData() {
      let chartData = this.chartOptions.chartData
      if (this.enableSelector) {
        chartData = chartData[this.selectedValue]
      }
      if (this.enableZoom && this.zoomMin > 0) {
        chartData = chartData.map(points => points.slice(this.zoomMin))
      }
      if (this.enableCumsum && this.isCumulativeDisplay) {
        chartData = chartData.map(points => {
          let cumsum = 0
          return points.map(point => {
            cumsum += point.y
            return {
              x: point.x,
              y: cumsum,
            }
          })
        })
      }
      return chartData
    },
    modifiedChartLabels() {
      let chartLabels = this.chartOptions.chartLabels
      if (!chartLabels || this.zoomMin == 0) {
        return chartLabels
      }
      return chartLabels.slice(this.zoomMin)
    },
    modifiedChartOptions() {
      return {
        ...this.chartOptions,
        chartData: this.modifiedChartData,
        chartLabels: this.modifiedChartLabels,
      }
    },
    zoomMax() {
      if (!this.modifiedChartData || !this.modifiedChartData.length) {
        return 0
      }
      return Math.max(0, this.modifiedChartData[0].length - 4)
    },
    selectorItems() {
      // if chartData is not an object, return an empty array
      if (typeof this.chartOptions.chartData !== 'object') {
        return []
      }
      return Object.keys(this.chartOptions.chartData)
    },
    selectedValue() {
      if (this.selectorItems.length === 0) {
        return null
      }
      return this.selectorItems[this.selectorIndex]
    },
  },
  watch: {
    isCumulativeDisplay(newVal) {
      ls.set(`isCumulativeDisplay_${this.$parent.$route.name}`, newVal)
    },
    useBarChart(newVal) {
      ls.set(`useBarChart_${this.$parent.$route.name}`, newVal)
    },
  },
  async created() {
    const local_isCumulativeDisplay = ls.get(
      `isCumulativeDisplay_${this.$parent.$route.name}`,
    )
    if (local_isCumulativeDisplay !== null) {
      this.isCumulativeDisplay = local_isCumulativeDisplay
    }
    const local_useBarChart = ls.get(`useBarChart_${this.$parent.$route.name}`)
    if (local_useBarChart !== null) {
      this.useBarChart = local_useBarChart
    }
  },
  methods: {},
}
</script>

<style scoped>
.description {
  display: flex;
  justify-content: space-between;
  color: var(--color-secondary);
  margin-bottom: 1em;
}
/* hide the thumb label - it looks bad for dates */
::v-deep .v-slider__thumb-label {
  display: none;
}
/* hide messages - just adds whitespace */
::v-deep .v-messages {
  display: none;
}
/* remove margin for input slot - just adds whitespace */
::v-deep .v-input__slot {
  margin-bottom: 0;
}
</style>
